import { Form } from '@edgebox/react-components';
import {
  ContentRepresentation,
  MonitoringPromptKpi,
  MonitoringPromptStatus,
  TaxonomyFacet,
  TaxonomyTermStatus,
} from '@edgebox/sync-core-data-definitions';
import { MonitoringPromptSummaryDto, MonitoringPromptSummaryListFilters } from '@edgebox/sync-core-rest-client';
import type { OpenApi } from '@edgebox/sync-core-rest-client';
import { faThumbsDown, faThumbsUp } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AxisBottom, AxisLeft, AxisRight } from '@visx/axis';
import { Group } from '@visx/group';
import { ParentSize } from '@visx/responsive';
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale';
import { LinePath } from '@visx/shape';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { SyncCoreApiContext } from '../../../contexts/SyncCoreApiContext';
import { sendToParent } from '../../../frame-messages';
import { AppParamsContext, IAppParams } from '../../AppParams';
import { GovernanceAnalyticsAttributes } from './Components/GovernanceAnalyticsAttributes';
import { GovernanceAnalyticsSources } from './Components/GovernanceAnalyticsSources';
import { FaviconLoadGate } from './Components/SourceFavicon';
import { SourceGrouping } from './Components/citationSources.utils';
import { GovernanceAnalyticsKpiCard } from './Components/GovernanceAnalyticsKpiCard';
import { GovernanceAnalyticsKpiCardRow } from './Components/GovernanceAnalyticsKpiCardRow';
import { PositionRankBadge } from './Components/PositionRankBadge';
import { BrandLandscapeCard } from './Components/BrandLandscapeCard';
import { GAPS_REFRESH_FAILED_MESSAGE } from './Components/BrandLandscapeCard.utils';
import { GovernanceAnalyticsSectionCard } from './Components/GovernanceAnalyticsSectionCard';
import { GovernanceSegmentedSwitch } from './Components/GovernanceSegmentedSwitch';
import { GovernanceSortableHeader, GovernanceSortDirection } from './Components/GovernanceSortableHeader';
import { AddFilterDropdown, IInventoryFilterValue } from './Onboarding/ContentInventory/Components/AddFilterDropdown';
import { useBrandPresenceScreenAddress, useReportBrandPresenceScreenQuery } from './brandPresenceScreenQuery';
import { AnalyticsScreenSkeleton } from './Components/AnalyticsScreenSkeleton';
import { TableRowsSkeleton, TrendChartSkeleton } from './Components/BrandPresenceSkeleton';
import { BrandPresencePageHeader } from './Components/BrandPresencePageHeader';
import { getBrandPresenceTabClassName } from './Components/BrandPresenceTabStyles';
import { BP_CARD_CLASSES, BP_TOOLBAR_BUTTON_CLASSES } from './Components/brandPresenceClasses';

/** The label that leads the filter bar. */
const FILTER_LABEL_CLASSES = 'font-eyebrow text-[0.625rem] font-medium uppercase tracking-[0.14em] text-slate-500';
import { SuggestedTermIcon } from './Onboarding/ContentInventory/Components/TaxonomyTermNameBadge';
import { getCachedTaxonomies, getCachedTaxonomyTermsByFacet } from './Onboarding/ContentInventory/Components/useCachedTaxonomyData';
import { TaxonomyTermIndexScope } from './Onboarding/ContentInventory/Components/TaxonomyTermIndexScope';
import { buildTermFilterOptions, isSuggestedTerm, termStatusPresentation } from './Components/termStatusPresentation';
import {
  buildPurposeTabs,
  isSelectedPurposeUntyped,
  PurposeTermLike,
  resolveSelectedPurposeKey,
  selectedPurposeTab,
} from './Components/purposeTabs.utils';
import { UntypedPurposeNotice } from './Components/UntypedPurposeNotice';
import { PurposeKpiScopeNote } from './Components/PurposeKpiScopeNote';
import { isKpiShown, isMetricShown, purposeKpiMask, resolveShownMetric, shownMetrics } from './Components/purposeKpiMask.utils';
import { termDetailPath, termRouteReference } from './GovernanceTaxonomyManagement.utils';
import { promptsForPurpose, resolveSelectedPromptKey, ScopablePrompt, scopablePromptsOf } from './Components/promptPurposeScoping.utils';
import { COARSE_HISTORY_BUCKETS, DAILY_HISTORY_BUCKETS, defaultVisibilityDisplayMode } from './Components/visibilityGranularity.utils';
import { BandedMetric, bandThresholdMarkers, isBandedMetric } from './Components/bandVisuals.utils';
import { MultiSeriesTrendChart, TREND_CHART_EMPTY_MESSAGE } from './Components/MultiSeriesTrendChart';
import { SentimentTone, VISIBLE_MENTION_FACETS } from './GovernanceAnalyticsAiAnswers.utils';
import {
  TONE_KPI,
  TONE_METRIC,
  TONE_METRIC_LABEL,
  TONE_METRIC_LABEL_LOWER,
  ToneMetricName,
  IToneBearingRow,
  toneValue,
} from './toneMetric';
import { TONE_BAND_METRIC } from './Components/bandVisuals.utils';
import { GapsMetricCell } from './Components/GapsMetricCell';

/** A term as the cached facet fetch returns it, before it becomes a filter choice. */
import { breakdownMetricPercentOf } from './Components/breakdownMetricBinding.utils';
import { toScorePercentage } from './Components/contentHealth.utils';
type SelectableTerm = { key?: unknown; name?: unknown; status?: TaxonomyTermStatus };
import {
  AiKpi,
  analyzedPromptRunCountOf,
  buildStatsFilters,
  buildUrlQueryFromState,
  FacetConfig,
  findConfiguredTerm,
  isDisplayableStatsTerm,
  isToneKpi,
  metricLabel,
  metricValue,
  AttributeView,
  averageOfMeasured,
  maximumOfMeasured,
  orderAndColorBreakdownSeries,
  parseAnalyticsQuery,
  rankSubjectsByPosition,
  resolveTermName,
  serializeFiltersParam,
  storedColorsFromFacetConfigs,
  subjectPositionOf,
} from './GovernanceAnalyticsAiVisibility.utils';
import { GOVERNANCE_COMPARISON_PALETTE, governanceAnalyticsPalette, readPrimaryColor, withAlpha } from './Components/governanceThemeColors';
import { holdStatsRequests, scheduleStatsRequest, StatsRequestPriority, statsRequestKey } from './Components/statsRequestQueue';
import {
  promptMonitoringDailyHistoryRequest,
  promptMonitoringMonthlyHistoryRequest,
  promptMonitoringTotalRequest,
  promptMonitoringWeeklyHistoryRequest,
} from './Components/promptMonitoringStatsRequests';
import { LoadWhenNearViewport, NearViewportBand, useNearViewport } from './Components/useNearViewport';
import { KPIS_LOAD_FAILED_MESSAGE, SectionLoadFailed, TRENDS_LOAD_FAILED_MESSAGE, useRetry } from './Components/SectionLoadFailed';

/** The room a lower section holds until it loads: about the height of a section card. */
const SECTION_PLACEHOLDER_CLASSES = 'h-80';

/** Said in place of the screen when the lists it is built from could not be loaded. */
const SCREEN_LOAD_FAILED_MESSAGE = 'AI Visibility could not be loaded.';
const BREAKDOWN_LOAD_FAILED_MESSAGE = 'The breakdown could not be loaded.';
const PROMPTS_LOAD_FAILED_MESSAGE = 'The prompts could not be loaded.';

// The bucket counts live beside the granularity itself, where they can be tested.
const HISTORY_BUCKETS = COARSE_HISTORY_BUCKETS;
const NO_TERM_ASSIGNED_LABEL = 'None';
const NULL_FACET_TERM_KEY = '__facet_none__';
/**
 * Non-brand series colours. The customer's own series always uses the theme's primary
 * (`readPrimaryColor()`); these only have to stay distinguishable from it and from each other.
 */
const [VISIBILITY_SERIES_COLOR, RECOMMENDATION_SERIES_COLOR, SENTIMENT_SERIES_COLOR] = GOVERNANCE_COMPARISON_PALETTE;

/**
 * The line-chart metrics, in the order Trends has always plotted them.
 *
 * Also the order the Breakdown switch offers, and the order its fallback picks from when the
 * reader's chosen metric is not one this purpose measures.
 */
const TREND_METRIC_ORDER: readonly AiKpi[] = ['visibility', 'shareOfVoice', TONE_METRIC, 'recommendation'];

const FACET_OPTIONS = [
  TaxonomyFacet.Brand,
  TaxonomyFacet.Competitor,
  TaxonomyFacet.Product,
  TaxonomyFacet.CompetitorProduct,
  TaxonomyFacet.CompetitorProgram,
  TaxonomyFacet.Site,
] as const;

const BREAKDOWN_FACETS = [
  TaxonomyFacet.Brand,
  TaxonomyFacet.Product,
  TaxonomyFacet.Site,
  TaxonomyFacet.Competitor,
  TaxonomyFacet.CompetitorProduct,
  TaxonomyFacet.CompetitorProgram,
] as const;

/**
 * Every facet whose tags this screen names, the purposes aside: its tag scope, loaded in one request
 * as it mounts.
 *
 * The engines and locales its filters offer; the Breakdown and Gaps facets (`FACET_OPTIONS`); the
 * organization and product attributes the Attributes module ranks, whose names resolve through the
 * shared term index; and the curated sources and the entities the Sources module credits citations
 * to (`VISIBLE_MENTION_FACETS`). Each of those readers asks for its facets through the same
 * per-facet cache, so each finds them loaded or on the way — a facet left out here would cost a
 * request of its own when its reader asks, and an attribute facet left out would resolve its keys as
 * missing. Naming the whole taxonomy is what the scope exists to avoid.
 *
 * The purposes are left out on purpose: a facet no scope declares is asked for on its own, at once,
 * and the headline figures wait for the purposes and for nothing else, so they must not wait for this
 * request's largest list.
 */
const SCREEN_TERM_FACETS: readonly TaxonomyFacet[] = Array.from(
  new Set([
    TaxonomyFacet.Engine,
    TaxonomyFacet.Locale,
    ...FACET_OPTIONS,
    TaxonomyFacet.OrganizationAttribute,
    TaxonomyFacet.ProductAttribute,
    TaxonomyFacet.AuthoritySource,
    ...VISIBLE_MENTION_FACETS,
  ])
);

type AnalyticsFacet = (typeof FACET_OPTIONS)[number];
type BreakdownFacet = (typeof BREAKDOWN_FACETS)[number];
type DisplayMode = 'day' | 'month' | 'week';
type OptionalFacet = AnalyticsFacet | 'none';
type TrendsComparisonMode = 'competitor' | 'average';
type BreakdownSeries = {
  id: string;
  label: string;
  color: string;
  /** `value` is absent where the metric was not measured — never zero-filled. */
  points: { x: string; value?: number }[];
};

/** Prompts per page of the lean prompt list: the most it answers with at once. */
const PROMPT_SUMMARY_PAGE_SIZE = 500;

const NON_PREVIOUS_PROMPT_STATUSES = [
  MonitoringPromptStatus.Draft,
  MonitoringPromptStatus.Active,
  MonitoringPromptStatus.Paused,
  MonitoringPromptStatus.Archived,
];

type StatsHistoryTerm = {
  term: string | null;
  count: number;
  visibility?: number | null;
  sentiment?: number | null;
  shareOfVoice?: number | null;
  recommendation?: number | null;
  competitorVisibility?: number | null;
  competitorSentiment?: number | null;
  competitorShareOfVoice?: number | null;
  competitorRecommendation?: number | null;
  averageVisibility?: number | null;
  averageSentiment?: number | null;
  averageShareOfVoice?: number | null;
  averageRecommendation?: number | null;
} & IToneBearingRow;

type StatsHistoryBucket = {
  identifier: string;
  count: number;
  visibility?: number | null;
  sentiment?: number | null;
  shareOfVoice?: number | null;
  recommendation?: number | null;
  competitorVisibility?: number | null;
  competitorSentiment?: number | null;
  competitorShareOfVoice?: number | null;
  competitorRecommendation?: number | null;
  averageVisibility?: number | null;
  averageSentiment?: number | null;
  averageShareOfVoice?: number | null;
  averageRecommendation?: number | null;
  terms?: StatsHistoryTerm[] | null;
} & IToneBearingRow;

type TrendRow = {
  id: string;
  label: string;
  count: number;
  visibility?: number;
  sentiment?: number;
  shareOfVoice?: number;
  recommendation?: number;
  competitorVisibility?: number;
  competitorSentiment?: number;
  competitorShareOfVoice?: number;
  competitorRecommendation?: number;
  averageVisibility?: number;
  averageSentiment?: number;
  averageShareOfVoice?: number;
  averageRecommendation?: number;
  terms?: {
    term: string | null;
    count: number;
    visibility?: number;
    sentiment?: number;
    shareOfVoice?: number;
    recommendation?: number;
    competitorVisibility?: number;
    competitorSentiment?: number;
    competitorShareOfVoice?: number;
    competitorRecommendation?: number;
    averageVisibility?: number;
    averageSentiment?: number;
    averageShareOfVoice?: number;
    averageRecommendation?: number;
  }[];
};

/**
 * One row of the Gaps table, after the screen has unwrapped the tag key and clamped the figures.
 *
 * Exported because the Brand landscape card reads these same rows, and narrowing its input from
 * this type is what keeps the two readers in step when a figure's shape changes here.
 */
// type-duplication-approved: the row AFTER this screen normalizes it, which the wire model no longer describes: `resolveFacetTerm` unwraps `term` from the client's `object | null` to a tag key, `normalizeScore` clamps each figure to [0, 1] and drops a non-finite one, `normalizeCount` rounds the counts, and `avgPosition` / `positionRunCount` are derived by `subjectPositionOf`. Narrowing the client model here would type every one of these as the raw shape it is not.
export type GapsRow = {
  term: string | null;
  count: number;
  /**
   * Distinct answers this subject was seen in. Absent when the wire carried none.
   *
   * Not the n behind Visibility or Sentiment: those are means over different sets of rows, and the
   * wire carries no denominator for either.
   */
  answerCount?: number;
  visibility?: number;
  sentiment?: number;
  shareOfVoice?: number;
  recommendation?: number;
  contentHealth?: number;
  /** Mean position across the answers that positioned this subject; absent when none did. */
  avgPosition?: number;
  /** How many answers that mean is drawn from. */
  positionRunCount?: number;
};

function normalizeScore(score?: number | null): number | undefined {
  if (typeof score !== 'number' || !Number.isFinite(score)) {
    return undefined;
  }

  return Math.max(0, Math.min(1, score));
}

/**
 * A whole count as it arrives from the wire.
 *
 * Deliberately not `normalizeScore`: that clamps to [0, 1], which would turn 148 answers into 1.
 */
function normalizeCount(count?: number | null): number | undefined {
  if (typeof count !== 'number' || !Number.isFinite(count)) {
    return undefined;
  }

  return Math.max(0, Math.round(count));
}

/** The calendar fields of a day, week or month bucket, as the client declares each of them. */
type HistoryBucketCalendar =
  | Pick<OpenApi.GovernancePromptMonitoringStatsDailyHistoryBucketResponse, 'year' | 'month' | 'day'>
  | Pick<OpenApi.GovernancePromptMonitoringStatsWeeklyHistoryBucketResponse, 'year' | 'week'>
  | Pick<OpenApi.GovernancePromptMonitoringStatsMonthlyHistoryBucketResponse, 'year' | 'month'>;

/**
 * One history bucket's axis label: the day, the ISO week or the month it counts.
 *
 * Read from the calendar fields Sync Core stamps on the bucket, in the zone it counted the bucket in,
 * never from the bucket's timestamp read in the reader's zone: a day starts at a midnight in Sync
 * Core's zone, and that midnight is still the evening before anywhere west of it.
 */
function historyBucketLabel(bucket: HistoryBucketCalendar): string {
  if ('day' in bucket) {
    // The parts go in and come out in UTC so that no zone can move them to a neighbouring day; the
    // reader's locale only decides how the day is written.
    return new Date(Date.UTC(bucket.year, bucket.month - 1, bucket.day)).toLocaleDateString(undefined, {
      month: 'numeric',
      day: 'numeric',
      timeZone: 'UTC',
    });
  }

  return 'week' in bucket ? `W${bucket.week} ${String(bucket.year).slice(-2)}` : `${bucket.month}/${String(bucket.year).slice(-2)}`;
}

function formatPercentage(value?: number): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '-';
  }

  return `${Math.round(Math.max(0, Math.min(1, value)) * 100)}%`;
}

function resolveFacetTerm(value: unknown): string | null | undefined {
  if (value === null) {
    return null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed.length) {
      return undefined;
    }

    if (trimmed.toLowerCase() === 'null') {
      return null;
    }

    return trimmed;
  }

  if (value && typeof value === 'object') {
    const candidate = value as { term?: unknown; value?: unknown; key?: unknown; id?: unknown; name?: unknown };

    if (candidate.term === null || candidate.value === null) {
      return null;
    }

    if (candidate.term !== undefined) {
      return resolveFacetTerm(candidate.term);
    }

    if (candidate.value !== undefined) {
      return resolveFacetTerm(candidate.value);
    }

    if (candidate.key !== undefined) {
      return resolveFacetTerm(candidate.key);
    }

    if (candidate.id !== undefined) {
      return resolveFacetTerm(candidate.id);
    }

    if (candidate.name !== undefined) {
      return resolveFacetTerm(candidate.name);
    }
  }

  return undefined;
}

function first3FacetTerms(rows: TrendRow[]): Array<string | null> {
  const countByTerm = new Map<string, number>();

  for (const row of rows) {
    for (const term of row.terms || []) {
      const bucketKey = term.term === null ? NULL_FACET_TERM_KEY : term.term;
      countByTerm.set(bucketKey, (countByTerm.get(bucketKey) || 0) + term.count);
    }
  }

  return Array.from(countByTerm.entries())
    .sort((left, right) => right[1] - left[1])
    .slice(0, 3)
    .map((entry) => (entry[0] === NULL_FACET_TERM_KEY ? null : entry[0]));
}

/** The facet a stats term belongs to, which is what decides whether it is the customer's own. */
function resolveTermFacet(configs: FacetConfig[], value: string): TaxonomyFacet | undefined {
  for (const config of configs) {
    if (config.terms.some((term) => term.key === value || term.id === value || term.name === value)) {
      return config.facet;
    }
  }
  return undefined;
}

/**
 * A stats row's subject, as text.
 *
 * Deliberately NOT a badge. A chip is chrome for something you can act on — a filter you can
 * remove, a category you can recognise at a glance among others. The subject of a table row is
 * none of those: it is the row's identity, and boxing an identity makes a table of rows read as a
 * table of tags. Status treatment (muting, the suggestion lightbulb) still applies, through the
 * same shared helper — that is presentation of the term's state, not chrome around its name.
 *
 * Named from the facet lists this screen already holds rather than through the shared term index:
 * every row that reaches here passed `isDisplayableStatsTerm`, so its term is in those lists with
 * its name and status, and asking the index for it would only widen what the index has to fetch.
 */
function FacetTermName({ facetConfigs, term }: { facetConfigs: FacetConfig[]; term: string | null }) {
  if (term === null) {
    // The explicit "no term" bucket is a real category, but it is still this row's subject, so it
    // reads as text like every sibling — muted, because it names an absence.
    return <span className="text-slate-500">{NO_TERM_ASSIGNED_LABEL}</span>;
  }

  const configured = findConfiguredTerm(facetConfigs, term);
  const presentation = termStatusPresentation(configured?.status, 'table-row');
  // An archived term is not part of what the customer is looking at — the same backstop the shared
  // name component applies.
  if (presentation.hidden) {
    return null;
  }

  const name = configured?.name ?? term;
  return (
    <span className={presentation.className || undefined} title={presentation.title ?? name}>
      {name}
      {presentation.showSuggestionIcon ? <SuggestedTermIcon /> : null}
    </span>
  );
}

function TrendsVisualization({
  rows,
  selectedFacet,
  selectedKpi,
  comparisonMode,
  facetConfigs,
  metrics,
}: {
  rows: TrendRow[];
  selectedFacet: OptionalFacet;
  selectedKpi: AiKpi;
  comparisonMode: TrendsComparisonMode;
  facetConfigs: FacetConfig[];
  /**
   * The metrics this purpose measures, in plotting order.
   *
   * A metric the purpose fixes by construction gets no line and no comparison line — a flat 100 %
   * visibility band drawn beside a real measurement invites the reader to compare the two.
   */
  metrics: readonly AiKpi[];
}) {
  const primaryColor = useMemo(() => readPrimaryColor(), []);
  const analyticsPalette = useMemo(() => governanceAnalyticsPalette(primaryColor), [primaryColor]);

  if (!rows.length) {
    return <div className="text-sm text-slate-500">{TREND_CHART_EMPTY_MESSAGE}</div>;
  }

  const facetTerms = selectedFacet === 'none' ? [] : first3FacetTerms(rows);
  // Both modes are means across MANY entities, so neither can carry one brand's name — but the
  // label has to say so. "Competitor" (singular) read as one named rival whose identity had been
  // withheld; it is the average across every tracked competitor.
  const selectedComparisonLabel = comparisonMode === 'competitor' ? 'Competitor average' : 'All brands average';

  const comparisonMetricValue = (
    entry: {
      competitorVisibility?: number;
      competitorSentiment?: number;
      competitorShareOfVoice?: number;
      competitorRecommendation?: number;
      averageVisibility?: number;
      averageSentiment?: number;
      averageShareOfVoice?: number;
      averageRecommendation?: number;
    },
    metric: AiKpi
  ): number | undefined => {
    if (comparisonMode === 'competitor') {
      if (metric === 'visibility') {
        return normalizeScore(entry.competitorVisibility);
      }
      if (isToneKpi(metric)) {
        return normalizeScore(entry.competitorSentiment);
      }
      if (metric === 'recommendation') {
        return normalizeScore(entry.competitorRecommendation);
      }
      return normalizeScore(entry.competitorShareOfVoice);
    }

    if (metric === 'visibility') {
      return normalizeScore(entry.averageVisibility);
    }
    if (isToneKpi(metric)) {
      return normalizeScore(entry.averageSentiment);
    }
    if (metric === 'recommendation') {
      return normalizeScore(entry.averageRecommendation);
    }
    return normalizeScore(entry.averageShareOfVoice);
  };

  // One entry per metric the purpose measures, in the order the panel has always plotted them.
  // The own line and its comparison twin are one unit: dropping only one of the pair would leave a
  // comparison against a line that is not drawn.
  //
  // A bucket that measured nothing for a line stays undefined, and the chart breaks the line there.
  // A zero in its place would plot "no answers this week" as a measured 0% — on the verdict line,
  // the bottom of the harshest band. A measured zero is still 0.
  const metricSeries: Record<AiKpi, { label: string; color: string; own: (row: TrendRow) => number | undefined }> = {
    visibility: { label: 'Visibility', color: VISIBILITY_SERIES_COLOR, own: (row) => row.visibility },
    // Share of Voice is the customer's own headline metric, so it carries the theme colour.
    shareOfVoice: { label: 'Share of Voice', color: primaryColor, own: (row) => row.shareOfVoice },
    sentiment: { label: TONE_METRIC_LABEL, color: SENTIMENT_SERIES_COLOR, own: (row) => row.sentiment },
    verdict: { label: TONE_METRIC_LABEL, color: SENTIMENT_SERIES_COLOR, own: (row) => row.sentiment },
    recommendation: { label: 'Recommendation', color: RECOMMENDATION_SERIES_COLOR, own: (row) => row.recommendation },
  };

  const series =
    selectedFacet === 'none'
      ? metrics.flatMap((metric) => {
          const entry = metricSeries[metric];
          return [
            {
              label: entry.label,
              color: entry.color,
              line: rows.map((row) => ({
                x: row.label,
                value: toScorePercentage(entry.own(row)),
              })),
            },
            {
              label: `${entry.label} (${selectedComparisonLabel})`,
              color: withAlpha(entry.color, 0.5),
              line: rows.map((row) => ({
                x: row.label,
                value: toScorePercentage(comparisonMetricValue(row, metric)),
              })),
            },
          ];
        })
      : facetTerms
          .filter((term) => isDisplayableStatsTerm(facetConfigs, term))
          .map((term, index) => ({
            label: term === null ? NO_TERM_ASSIGNED_LABEL : resolveTermName(facetConfigs, term),
            color: analyticsPalette[index % analyticsPalette.length],
            line: rows.map((row) => {
              const matched = row.terms?.find((entry) => entry.term === term);
              return { x: row.label, value: matched ? breakdownMetricPercentOf(matched, selectedKpi) : undefined };
            }),
          }));

  // Trends plots the banded series ALONGSIDE unbanded ones — comparison averages, recommendation.
  // So the bands get their own axis on the right, and the left axis stays a plain percentage scale
  // that applies to every series in the panel.
  return (
    <MultiSeriesTrendChart
      series={series.map((entry) => ({ label: entry.label, color: entry.color, points: entry.line }))}
      bandMetric={isBandedMetric(selectedKpi) ? selectedKpi : undefined}
      bandAxisSide="right"
    />
  );
}

function BreakdownVisualization({
  series,
  kpi,
  breakdownEnabled,
}: {
  series: BreakdownSeries[];
  kpi: AiKpi;
  /** Whether the reader has a breakdown switched on. With one on, an empty chart is a window without data. */
  breakdownEnabled: boolean;
}) {
  if (!breakdownEnabled) {
    return <div className="text-sm text-slate-500">Enable at least one breakdown to see trends per brand or attribute.</div>;
  }

  return (
    <MultiSeriesTrendChart
      series={series.map((entry) => ({ label: entry.label, color: entry.color, points: entry.points }))}
      bandMetric={isBandedMetric(kpi) ? kpi : undefined}
    />
  );
}

/** The screen, inside the tag scope it declares. The body follows. */
export function GovernanceAnalyticsAiVisibility() {
  return (
    <TaxonomyTermIndexScope facets={SCREEN_TERM_FACETS}>
      <GovernanceAnalyticsAiVisibilityScreen />
    </TaxonomyTermIndexScope>
  );
}

function GovernanceAnalyticsAiVisibilityScreen() {
  const navigate = useNavigate();
  const apiContext = useContext(SyncCoreApiContext);
  const api = apiContext?.api;
  const appParams = React.useContext(AppParamsContext) as IAppParams | null;
  const primaryColor = useMemo(() => readPrimaryColor(), []);
  const analyticsPalette = useMemo(() => governanceAnalyticsPalette(primaryColor), [primaryColor]);
  // Selected facet chips are tinted from the theme primary rather than from hardcoded hexes.
  const activeFacetChipStyle = useMemo<React.CSSProperties>(
    () => ({ borderColor: primaryColor, backgroundColor: withAlpha(primaryColor, 0.12), color: primaryColor }),
    [primaryColor]
  );
  const { query: requestedQuery, path: requestedPath } = useBrandPresenceScreenAddress();
  const parsedAnalyticsQuery = useMemo(() => parseAnalyticsQuery(requestedQuery, requestedPath), [requestedQuery, requestedPath]);
  const parsedAnalyticsQuerySerialized = useMemo(() => JSON.stringify(parsedAnalyticsQuery), [parsedAnalyticsQuery]);
  const [displayMode, setDisplayMode] = useState<DisplayMode>(() => parsedAnalyticsQuery.displayMode ?? 'month');
  // Whether the age lookup below has had its one chance to correct `displayMode` — successfully,
  // unsuccessfully, or because there was nothing to look up. `displayMode` itself starts on a
  // provisional 'month' default that is frequently wrong, so a stats request keyed on it before this
  // is true would fetch the wrong granularity, and the correction would fetch it again once the
  // lookup lands. Trends and Breakdown wait for this the same way the headline figures wait for
  // `purposeResolved` — one settled input instead of a provisional one followed by a correction.
  const [projectCreatedAtResolved, setProjectCreatedAtResolved] = useState(false);
  // An explicit choice — from the URL or from the switch — outranks the age-derived default forever
  // after, so the default never yanks the view back once the reader has decided.
  const displayModeChosenRef = React.useRef<boolean>(!!parsedAnalyticsQuery.displayMode);
  // True once `displayMode` cannot change again on its own: either the reader chose it, or the age
  // lookup that would otherwise correct it has already had its say.
  const displayModeSettled = displayModeChosenRef.current || projectCreatedAtResolved;
  // Purely a display choice, and deliberately NOT part of statsFilters: every row it can hide is
  // already loaded, so flipping it must never cost a request. It lives here only because this
  // component owns the URL; the switch itself sits in the Attributes header where its rows are.
  const [hideSuggestions, setHideSuggestions] = useState<boolean>(parsedAnalyticsQuery.hideSuggestions);
  // Deep-linkable, so "look at this shape" is a shareable link rather than an instruction.
  const [attributeView, setAttributeView] = useState<AttributeView>(parsedAnalyticsQuery.attributeView);
  // How the Sources module groups cited websites. Deep-linked under the same name the answers list
  // uses, so the reading carries across the drill-down in both directions.
  const [sourceGrouping, setSourceGrouping] = useState<SourceGrouping>(parsedAnalyticsQuery.sourceGrouping);
  const projectUuid = appParams?.pageOptions?.projectUuid as string | undefined;

  // The project's age decides the opening granularity: a project with days of history has nothing
  // to show bucketed by month, and one with a year of history is unreadable bucketed by day.
  //
  // The correction and `projectCreatedAtResolved` are set together, from the one place `createdAt`
  // is known, rather than a second effect reacting to a `projectCreatedAt` state change. Splitting
  // them across two effects left a one-render gap where `projectCreatedAtResolved` had already
  // flipped true but `displayMode` had not yet been corrected — long enough for Trends and Breakdown
  // to read the guard as open and fire once on the stale provisional default, then again once the
  // correction landed.
  useEffect(() => {
    let active = true;
    if (!apiContext?.api || !projectUuid) {
      // Nothing to look up, so there is nothing that could still correct `displayMode` — the
      // provisional default is final already.
      setProjectCreatedAtResolved(true);
      return () => {
        active = false;
      };
    }

    void (async () => {
      let createdAt: number | undefined;
      try {
        const project = await apiContext.api.service.projects.itemByUuid(projectUuid as never);
        const createdAtValue = (project as unknown as { createdAt?: number | string })?.createdAt;
        if (createdAtValue !== undefined) {
          createdAt = new Date(createdAtValue).getTime();
        }
      } catch {
        // Without a creation date the default stays months, which can always render. A failed
        // lookup must not blank the page.
      }

      if (!active) {
        return;
      }
      // Only while the reader has not chosen a granularity for themselves — an explicit choice
      // outranks the age-derived default forever after.
      if (createdAt !== undefined && !displayModeChosenRef.current) {
        setDisplayMode(defaultVisibilityDisplayMode(createdAt));
      }
      setProjectCreatedAtResolved(true);
    })();

    return () => {
      active = false;
    };
  }, [apiContext?.api, projectUuid]);

  /** Records that the granularity was chosen deliberately, so the default stops applying. */
  const chooseDisplayMode = React.useCallback((next: DisplayMode) => {
    displayModeChosenRef.current = true;
    setDisplayMode(next);
  }, []);
  const lastSentQuerySerializedRef = React.useRef<string | undefined>(undefined);
  const isHydratedFromIncomingRef = React.useRef(false);
  const initialHydratedQuerySerializedRef = React.useRef<string | undefined>(undefined);

  const [loading, setLoading] = useState(true);
  // The lists the screen is built from could not be loaded; the screen says so instead of showing
  // its skeleton for good.
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadAttempt, retryLoad] = useRetry();
  const [promptOptionsFailed, setPromptOptionsFailed] = useState(false);
  const [promptOptionsAttempt, retryPromptOptions] = useRetry();
  const [selectedFilters, setSelectedFilters] = useState<IInventoryFilterValue[]>(() => parsedAnalyticsQuery.selectedFilters);
  // Read back from the address like the purpose and the tags, so Back, a reload and a shared link
  // reopen the screen narrowed the way the reader left it.
  const [selectedEngineKey, setSelectedEngineKey] = useState(() => parsedAnalyticsQuery.engineKey);
  const [selectedLocaleKey, setSelectedLocaleKey] = useState(() => parsedAnalyticsQuery.localeKey);
  const [selectedPromptKey, setSelectedPromptKey] = useState(() => parsedAnalyticsQuery.promptKey);
  const [selectedPurposeTermKey, setSelectedPurposeTermKey] = useState(() => parsedAnalyticsQuery.purposeTermKey);
  // Raw terms are held and the options derived, so a filter pointing at a since-archived term keeps
  // it as a visible choice instead of being silently reset.
  const [engineTerms, setEngineTerms] = useState<SelectableTerm[]>([]);
  const [localeTerms, setLocaleTerms] = useState<SelectableTerm[]>([]);
  const [promptOptions, setPromptOptions] = useState<ScopablePrompt[]>([]);
  const [purposeTerms, setPurposeTerms] = useState<PurposeTermLike[]>([]);
  // Set once the purposes have arrived, whether or not there are any: the page-level figures wait
  // for this and for nothing else.
  const [purposeTermsLoaded, setPurposeTermsLoaded] = useState(false);
  const [facetConfigs, setFacetConfigs] = useState<FacetConfig<AnalyticsFacet>[]>([]);
  // Stored brand colours for the charted terms, from the same per-facet lists the screen reads names
  // and facets from — so a competitor's own colour paints its trend series where it has one.
  const storedTermColors = useMemo(() => storedColorsFromFacetConfigs(facetConfigs), [facetConfigs]);

  const [trendsFacet, setTrendsFacet] = useState<OptionalFacet>('none');
  const [trendsKpi, setTrendsKpi] = useState<AiKpi>('visibility');
  const [trendsComparisonMode, setTrendsComparisonMode] = useState<TrendsComparisonMode>('competitor');
  // Undefined until Trends has an answer: an answer without buckets is not the same as none yet.
  const [trendsRows, setTrendsRows] = useState<TrendRow[] | undefined>(undefined);
  const [breakdownFacets, setBreakdownFacets] = useState<BreakdownFacet[]>([]);
  const [breakdownKpi, setBreakdownKpi] = useState<AiKpi>('visibility');
  // The Breakdown's answer as it came, with each bucket's label; the series drawn from it are derived
  // below, so a metric or colour change never asks again.
  const [breakdownHistory, setBreakdownHistory] = useState<{ labels: string[]; buckets: StatsHistoryBucket[] } | undefined>(undefined);

  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryFailed, setSummaryFailed] = useState(false);
  const [summaryAttempt, retrySummary] = useRetry();
  // Set once the first request for the headline figures has settled, and never cleared.
  const [headlineRequestSettled, setHeadlineRequestSettled] = useState(false);
  const [promptListDue, setPromptListDue] = useState(false);
  const [trendsLoading, setTrendsLoading] = useState(false);
  const [trendsFailed, setTrendsFailed] = useState(false);
  const [trendsAttempt, retryTrends] = useRetry();
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [breakdownFailed, setBreakdownFailed] = useState(false);
  const [breakdownAttempt, retryBreakdown] = useRetry();
  const [gapsLoading, setGapsLoading] = useState(false);
  // A rejected refresh, so the Gaps table and the Brand landscape card can say the figures could not
  // be refreshed rather than reading as a selection nobody measured.
  const [gapsError, setGapsError] = useState(false);
  const [gapsAttempt, retryGaps] = useRetry();

  const [avgVisibility, setAvgVisibility] = useState<number | undefined>(undefined);
  const [avgSentiment, setAvgSentiment] = useState<number | undefined>(undefined);
  const [avgShareOfVoice, setAvgShareOfVoice] = useState<number | undefined>(undefined);
  const [avgRecommendation, setAvgRecommendation] = useState<number | undefined>(undefined);
  /**
   * The analyzed prompt runs the window matched — one run is one AI answer.
   *
   * NOT `/total`'s `count`, which aggregates one document per monitoring PROMPT. That figure under an
   * answers label is what made the KPI card and every "of N answers" sentence on this page disagree
   * with the per-run numerators beside them.
   *
   * undefined = a Sync Core too old to report it. Not 0, which would assert that nothing had been
   * analyzed and hide the Sources card on the strength of a figure that was never returned.
   */
  const [analyzedPromptRunCount, setAnalyzedPromptRunCount] = useState<number | undefined>(undefined);

  const [gapsFacets, setGapsFacets] = useState<BreakdownFacet[]>([]);
  const [gapsRows, setGapsRows] = useState<GapsRow[]>([]);
  const [gapsSortBy, setGapsSortBy] = useState<
    'term' | 'count' | 'visibility' | ToneMetricName | 'shareOfVoice' | 'recommendation' | 'contentHealth'
  >('visibility');
  const [gapsSortDirection, setGapsSortDirection] = useState<GovernanceSortDirection>('desc');

  const selectedFiltersSerialized = useMemo(() => serializeFiltersParam(selectedFilters), [selectedFilters]);
  // Archived terms are excluded by the fetch; a still-selected one is kept so the filter in effect
  // stays visible. Suggestions are marked.
  const engineOptions = useMemo(() => buildTermFilterOptions(engineTerms, selectedEngineKey), [engineTerms, selectedEngineKey]);
  const localeOptions = useMemo(() => buildTermFilterOptions(localeTerms, selectedLocaleKey), [localeTerms, selectedLocaleKey]);
  // Purposes are a small ordered set an operator works through, so they read as tabs: the choice
  // stays visible and the most important purpose is the one the page opens on.
  const purposeTabs = useMemo(() => buildPurposeTabs(purposeTerms, selectedPurposeTermKey), [purposeTerms, selectedPurposeTermKey]);
  // Every other figure on this screen is already filtered to the active purpose. Offering prompts
  // from other purposes let the two filters contradict each other and report the empty intersection
  // as a finding.
  const scopedPromptOptions = useMemo(
    () => promptsForPurpose(promptOptions, selectedPurposeTermKey),
    [promptOptions, selectedPurposeTermKey]
  );

  const statsFilters = useMemo(
    () => buildStatsFilters(selectedFilters, selectedEngineKey, selectedLocaleKey, selectedPromptKey, selectedPurposeTermKey),
    [selectedFilters, selectedEngineKey, selectedLocaleKey, selectedPromptKey, selectedPurposeTermKey]
  );
  // The page never loads unfiltered — mixing purposes averages metrics that are not comparable —
  // so it opens on the highest-priority purpose unless the URL named one.
  useEffect(() => {
    if (!purposeTabs.length) {
      return;
    }
    const resolved = resolveSelectedPurposeKey(purposeTabs, selectedPurposeTermKey);
    if (resolved && resolved !== selectedPurposeTermKey) {
      setSelectedPurposeTermKey(resolved);
    }
  }, [purposeTabs, selectedPurposeTermKey]);

  // A prompt belonging to a purpose the reader has left is cleared rather than left filtering
  // silently. Runs only once the prompt list has loaded, so a selection is never cleared against a
  // list that has not arrived yet.
  useEffect(() => {
    if (!promptOptions.length) {
      return;
    }
    setSelectedPromptKey((current) => resolveSelectedPromptKey(promptOptions, selectedPurposeTermKey, current));
  }, [promptOptions, selectedPurposeTermKey]);

  const hasSelectedPurpose = !!selectedPurposeTermKey;
  const selectedPurpose = useMemo(() => selectedPurposeTab(purposeTabs, selectedPurposeTermKey), [purposeTabs, selectedPurposeTermKey]);
  // A tag written before purpose types existed has never said which of its KPIs are measurements, so
  // its tab is blocked — not rendered with every KPI — until an operator types it. Nothing is fetched
  // for it either: there is no honest figure to show.
  const selectedPurposeUntyped = isSelectedPurposeUntyped(purposeTabs, selectedPurposeTermKey);
  const kpisUnlocked = hasSelectedPurpose && !selectedPurposeUntyped;
  // The headline figures and the trend lines need nothing but a purpose that resolved to a tab —
  // not the facet lists, not the prompts — so they ask as soon as it has. On the screen's first
  // load their requests then wait in the held queue for the sections that need the lists (see
  // `firstFiguresAssembling`).
  const purposeResolved = purposeTermsLoaded && !!selectedPurpose;
  // Whether the headline figures are in, or will never be asked for (no purpose to measure, or one
  // that is blocked): the first stage of the screen, which the Prompt filter's list waits for.
  const headlineSettled = headlineRequestSettled || (purposeTermsLoaded && (!purposeTabs.length || selectedPurposeUntyped));
  const blockedPurposeTab = selectedPurposeUntyped ? selectedPurpose : undefined;
  // The tag's key where it has one, its id otherwise — the same reference the term list's own rows
  // link with, so the notice and the list send the reader to the same URL.
  const blockedPurposeReference = termRouteReference(blockedPurposeTab);
  const blockedPurposePath = blockedPurposeReference
    ? termDetailPath(TaxonomyFacet.MonitoringPromptPurpose, blockedPurposeReference)
    : undefined;

  /**
   * What this purpose measures — derived once, and asked per card, column and metric option below.
   *
   * A suppressed KPI is absent, never blank and never zero: the question already fixed its value, so
   * there is no measurement to draw and a placeholder would claim there was one.
   */
  const kpiMask = useMemo(() => purposeKpiMask(selectedPurpose), [selectedPurpose]);
  const showsVisibility = isKpiShown(kpiMask, MonitoringPromptKpi.Visibility);
  const showsShareOfVoice = isKpiShown(kpiMask, MonitoringPromptKpi.ShareOfVoice);
  const showsPosition = isKpiShown(kpiMask, MonitoringPromptKpi.Position);
  const showsSentiment = isKpiShown(kpiMask, TONE_KPI);
  const showsRecommendation = isKpiShown(kpiMask, MonitoringPromptKpi.Recommendation);
  const showsAttributes = isKpiShown(kpiMask, MonitoringPromptKpi.Attributes);
  // The metrics the two line charts may plot. Empty when a purpose measures only KPIs no chart
  // draws (position, attributes) — then the panels themselves have nothing to show.
  const chartMetrics = useMemo(() => shownMetrics(kpiMask, TREND_METRIC_ORDER), [kpiMask]);
  const breakdownMetricOptions = useMemo(
    () => chartMetrics.map((metric) => ({ value: metric, label: metricLabel(metric) })),
    [chartMetrics]
  );
  /**
   * The metric each panel actually reads, which is never one this purpose does not measure.
   *
   * Derived rather than written back into the state: the reader's own choice is kept as they made
   * it, so returning to a purpose that measures it restores it — but nothing is ever fetched or
   * plotted for a metric the purpose fixes by construction.
   */
  const shownTrendsKpi = resolveShownMetric(kpiMask, trendsKpi, TREND_METRIC_ORDER);
  const shownBreakdownKpi = resolveShownMetric(kpiMask, breakdownKpi, TREND_METRIC_ORDER);
  // Same rule for the Gaps table's sort: a column that is not rendered cannot order it.
  const shownGapsSortBy =
    gapsSortBy === 'term' || gapsSortBy === 'count' || gapsSortBy === 'contentHealth' || isMetricShown(kpiMask, gapsSortBy)
      ? gapsSortBy
      : (chartMetrics[0] ?? 'count');

  React.useEffect(() => {
    const parsedFiltersSerialized = serializeFiltersParam(parsedAnalyticsQuery.selectedFilters);

    if (parsedFiltersSerialized !== selectedFiltersSerialized) {
      setSelectedFilters(parsedAnalyticsQuery.selectedFilters);
    }

    if (parsedAnalyticsQuery.purposeTermKey !== selectedPurposeTermKey) {
      setSelectedPurposeTermKey(parsedAnalyticsQuery.purposeTermKey);
    }

    if (parsedAnalyticsQuery.engineKey !== selectedEngineKey) {
      setSelectedEngineKey(parsedAnalyticsQuery.engineKey);
    }

    if (parsedAnalyticsQuery.localeKey !== selectedLocaleKey) {
      setSelectedLocaleKey(parsedAnalyticsQuery.localeKey);
    }

    if (parsedAnalyticsQuery.promptKey !== selectedPromptKey) {
      setSelectedPromptKey(parsedAnalyticsQuery.promptKey);
    }

    const hydratedQuery = buildUrlQueryFromState({
      selectedFilters: parsedAnalyticsQuery.selectedFilters,
      purposeTermKey: parsedAnalyticsQuery.purposeTermKey,
      engineKey: parsedAnalyticsQuery.engineKey,
      localeKey: parsedAnalyticsQuery.localeKey,
      promptKey: parsedAnalyticsQuery.promptKey,
    });
    const hydratedSerialized = JSON.stringify(hydratedQuery);
    initialHydratedQuerySerializedRef.current = hydratedSerialized;
    lastSentQuerySerializedRef.current = hydratedSerialized;
    isHydratedFromIncomingRef.current = true;
  }, [parsedAnalyticsQuerySerialized]);

  React.useEffect(() => {
    if (!isHydratedFromIncomingRef.current) {
      return;
    }

    const nextQuery = buildUrlQueryFromState({
      selectedFilters,
      purposeTermKey: selectedPurposeTermKey,
      engineKey: selectedEngineKey,
      localeKey: selectedLocaleKey,
      promptKey: selectedPromptKey,
      // Only a deliberate choice is written. Serialising the derived default would freeze a young
      // project's granularity into every link shared from it.
      ...(displayModeChosenRef.current ? { displayMode } : {}),
      hideSuggestions,
      attributeView,
      sourceGrouping,
    });
    const nextSerialized = JSON.stringify(nextQuery);

    if (initialHydratedQuerySerializedRef.current === nextSerialized && lastSentQuerySerializedRef.current === nextSerialized) {
      return;
    }

    if (lastSentQuerySerializedRef.current === nextSerialized) {
      return;
    }

    lastSentQuerySerializedRef.current = nextSerialized;
    sendToParent({ type: 'update-query', query: nextQuery });
    // The granularity and the three display toggles are dependencies too. They were read but not
    // watched, so a link shared after flipping one carried the OTHER reader's state — the very
    // thing serialising them was meant to prevent. The effect is guarded against redundant sends,
    // so watching them costs nothing.
  }, [
    selectedFiltersSerialized,
    selectedPurposeTermKey,
    selectedEngineKey,
    selectedLocaleKey,
    selectedPromptKey,
    displayMode,
    hideSuggestions,
    attributeView,
    sourceGrouping,
  ]);

  // The same query the host page keeps, reported for the Settings row's Back — so Back restores the
  // purpose, the tags, the Engine, Locale and Prompt filters, a chosen granularity and the display
  // toggles the way it does on every other screen of the area.
  const screenQuery = useMemo(
    () =>
      buildUrlQueryFromState({
        selectedFilters,
        purposeTermKey: selectedPurposeTermKey,
        engineKey: selectedEngineKey,
        localeKey: selectedLocaleKey,
        promptKey: selectedPromptKey,
        ...(displayModeChosenRef.current ? { displayMode } : {}),
        hideSuggestions,
        attributeView,
        sourceGrouping,
      }),
    [
      selectedFilters,
      selectedPurposeTermKey,
      selectedEngineKey,
      selectedLocaleKey,
      selectedPromptKey,
      displayMode,
      hideSuggestions,
      attributeView,
      sourceGrouping,
    ]
  );
  useReportBrandPresenceScreenQuery(screenQuery);

  React.useEffect(() => {
    if (!api) {
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);
    setLoadFailed(false);
    void (async () => {
      // Every request below starts in the same tick: none of them needs another's answer, so the
      // bootstrap costs one round trip rather than a chain of eleven. Fetched one after the other,
      // the same requests kept the whole page — every stats request included — waiting for the sum.
      // The other tag lists read here travel in the screen's one tag request, which the tag scope
      // sends in this same pass (`SCREEN_TERM_FACETS`).
      //
      // The purposes, which the scope does not declare, go on their own and are applied the moment
      // they arrive. The headline figures need nothing but the selected purpose, so they must not
      // wait for the other tag lists, the taxonomies or the facet lists built from them.
      const purposeTermsRequest = getCachedTaxonomyTermsByFacet(api, TaxonomyFacet.MonitoringPromptPurpose).then((purposeTerms) => {
        if (!active) {
          return;
        }
        setPurposeTerms(purposeTerms);
        // A purpose that is no longer offered is cleared — but an archived one the user deep-linked to
        // is still offered (see buildTermFilterOptions), so this no longer discards their filter.
        setSelectedPurposeTermKey((current) =>
          current && buildTermFilterOptions(purposeTerms, current).some((option) => option.key === current) ? current : ''
        );
        setPurposeTermsLoaded(true);
      });
      // The facet lists depend on which taxonomies exist, so they start as soon as that is known —
      // and only for facets that have a taxonomy, exactly as before.
      const facetConfigsRequest = getCachedTaxonomies(api).then((taxonomies) =>
        Promise.all(
          FACET_OPTIONS.flatMap((facet) => {
            const taxonomy = taxonomies.find((entry) => entry.facet === facet);
            if (!taxonomy) {
              return [];
            }
            return [
              getCachedTaxonomyTermsByFacet(api, facet).then((terms): FacetConfig<AnalyticsFacet> | undefined =>
                terms.length
                  ? {
                      facet,
                      taxonomyKey: String(taxonomy.key || ''),
                      taxonomyName: String(taxonomy.name || facet),
                      terms: terms
                        .map((term) => ({
                          id: term.id,
                          key: String(term.key || ''),
                          name: String(term.name || '').trim(),
                          status: term.status,
                          color:
                            String(term.color || '')
                              .trim()
                              .toLowerCase() || undefined,
                        }))
                        .filter((term) => term.key && term.name),
                    }
                  : undefined
              ),
            ];
          })
        )
      );
      try {
        const [engineTerms, localeTerms, facetConfigCandidates] = await Promise.all([
          getCachedTaxonomyTermsByFacet(api, TaxonomyFacet.Engine),
          getCachedTaxonomyTermsByFacet(api, TaxonomyFacet.Locale),
          facetConfigsRequest,
          purposeTermsRequest,
        ]);

        if (!active) {
          return;
        }

        setFacetConfigs(facetConfigCandidates.filter((config): config is FacetConfig<AnalyticsFacet> => !!config));
        setEngineTerms(engineTerms);
        setLocaleTerms(localeTerms);
      } catch {
        // One failed list leaves the screen nothing to be built from. It says so and offers to ask
        // again; the lists that did load stay cached, so trying again asks only for the rest.
        if (active) {
          setLoadFailed(true);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [api, loadAttempt]);

  React.useEffect(() => {
    if (!facetConfigs.length) {
      setBreakdownFacets([]);
      return;
    }

    const availableBreakdownFacets: BreakdownFacet[] = facetConfigs
      .filter((config) => BREAKDOWN_FACETS.includes(config.facet))
      .map((config) => config.facet as BreakdownFacet);
    const preferredBreakdownDefaultCandidates: BreakdownFacet[] = [TaxonomyFacet.Brand, TaxonomyFacet.Competitor];
    const preferredDefaults = preferredBreakdownDefaultCandidates.filter((facet) => availableBreakdownFacets.includes(facet));

    setBreakdownFacets((previous) => {
      const stillAvailable = previous.filter((facet) => availableBreakdownFacets.includes(facet));
      if (stillAvailable.length) {
        return stillAvailable;
      }

      return preferredDefaults.length ? preferredDefaults : availableBreakdownFacets.slice(0, 1);
    });
  }, [facetConfigs]);

  React.useEffect(() => {
    if (!facetConfigs.length) {
      setGapsFacets([]);
      return;
    }

    const availableGapsFacets: BreakdownFacet[] = facetConfigs
      .filter((config) => BREAKDOWN_FACETS.includes(config.facet))
      .map((config) => config.facet as BreakdownFacet);
    const preferredGapsDefaultCandidates: BreakdownFacet[] = [TaxonomyFacet.Brand, TaxonomyFacet.Competitor];
    const preferredDefaults = preferredGapsDefaultCandidates.filter((facet) => availableGapsFacets.includes(facet));

    setGapsFacets((previous) => {
      const stillAvailable = previous.filter((facet) => availableGapsFacets.includes(facet));
      if (stillAvailable.length) {
        return stillAvailable;
      }

      return preferredDefaults.length ? preferredDefaults : availableGapsFacets.slice(0, 1);
    });
  }, [facetConfigs]);

  // The first figures of the screen travel as ONE combined request. Its sections ask as the lists
  // each one needs arrive — the headline figures on the purposes, Trends and the Breakdown on the
  // project's age, the sections below on the tags — so the statistics queue is held until all of
  // them are in: what asked meanwhile and what asks as the screen stands leave together, instead of
  // the first to ask taking a free place alone. The headline figures give up their head start for
  // it: they leave once the last list is in and the wave has gathered, rather than before the
  // lists, and their answer comes with the wave's.
  //
  // No list holds the figures back for good: the hold ends when the lists fail (a failed age lookup
  // settles the granularity on its own), once the queue's longest hold is over
  // (`STATS_REQUEST_MAX_HOLD_MS`), and when the screen is left. A project change while the screen
  // still holds ends that hold and takes a new one, with its own longest hold, for the new project's
  // age.
  const firstFiguresAssembling = !loadFailed && (loading || !displayModeSettled);
  React.useEffect(() => {
    if (!firstFiguresAssembling) {
      return;
    }
    return holdStatsRequests();
  }, [firstFiguresAssembling, projectUuid]);

  React.useEffect(() => {
    if (!api || !purposeResolved || !kpisUnlocked) {
      setSummaryLoading(false);
      setSummaryFailed(false);
      return;
    }

    let active = true;
    // Aborted when the filter changes or the screen is left: the request is then dropped if it has
    // not been sent, and no longer holds a place for the requests of the new filter if it has.
    const superseded = new AbortController();

    void (async () => {
      setSummaryLoading(true);
      setSummaryFailed(false);
      try {
        const total = await scheduleStatsRequest({
          priority: StatsRequestPriority.Kpis,
          signal: superseded.signal,
          ...promptMonitoringTotalRequest(api, statsFilters),
        });

        if (!active) {
          return;
        }

        setAnalyzedPromptRunCount(analyzedPromptRunCountOf(total));
        setAvgVisibility(normalizeScore(total.visibility));
        setAvgSentiment(normalizeScore(toneValue(total, 'own')));
        setAvgShareOfVoice(normalizeScore(total.shareOfVoice));
        setAvgRecommendation(normalizeScore(total.recommendation));
      } catch {
        // The figures on the cards belong to the filter that last succeeded, so they are cleared
        // rather than left to read as this one's.
        if (active) {
          setAnalyzedPromptRunCount(undefined);
          setAvgVisibility(undefined);
          setAvgSentiment(undefined);
          setAvgShareOfVoice(undefined);
          setAvgRecommendation(undefined);
          setSummaryFailed(true);
        }
      } finally {
        if (active) {
          setSummaryLoading(false);
          setHeadlineRequestSettled(true);
        }
      }
    })();

    return () => {
      active = false;
      superseded.abort();
    };
  }, [api, purposeResolved, statsFilters, kpisUnlocked, summaryAttempt]);

  React.useEffect(() => {
    // Also waits on `displayModeSettled`: firing on the provisional 'month' default and again once
    // the age lookup corrects it would request two granularities for the same purpose.
    if (!api || !purposeResolved || !kpisUnlocked || !displayModeSettled) {
      setTrendsLoading(false);
      setTrendsFailed(false);
      return;
    }

    let active = true;
    // See the headline figures above.
    const superseded = new AbortController();

    void (async () => {
      setTrendsLoading(true);
      setTrendsFailed(false);
      try {
        const filters = {
          ...statsFilters,
        };

        const history =
          displayMode === 'day'
            ? await scheduleStatsRequest({
                priority: StatsRequestPriority.Trends,
                signal: superseded.signal,
                ...promptMonitoringDailyHistoryRequest(api, { days: DAILY_HISTORY_BUCKETS, filters: filters }),
              })
            : displayMode === 'month'
              ? await scheduleStatsRequest({
                  priority: StatsRequestPriority.Trends,
                  signal: superseded.signal,
                  ...promptMonitoringMonthlyHistoryRequest(api, { months: HISTORY_BUCKETS, filters: filters }),
                })
              : await scheduleStatsRequest({
                  priority: StatsRequestPriority.Trends,
                  signal: superseded.signal,
                  ...promptMonitoringWeeklyHistoryRequest(api, { weeks: HISTORY_BUCKETS, filters: filters }),
                });

        if (!active) {
          return;
        }

        // Read off the client's own bucket types, which declare each granularity's calendar fields.
        const labels = history.map(historyBucketLabel);
        const rows = (history as StatsHistoryBucket[]).map((entry, index) => ({
          id: entry.identifier,
          label: labels[index],
          count: entry.count,
          visibility: normalizeScore(entry.visibility),
          sentiment: normalizeScore(toneValue(entry, 'own')),
          shareOfVoice: normalizeScore(entry.shareOfVoice),
          recommendation: normalizeScore(entry.recommendation),
          competitorVisibility: normalizeScore(entry.competitorVisibility),
          competitorSentiment: normalizeScore(toneValue(entry, 'competitor')),
          competitorShareOfVoice: normalizeScore(entry.competitorShareOfVoice),
          competitorRecommendation: normalizeScore(entry.competitorRecommendation),
          averageVisibility: normalizeScore(entry.averageVisibility),
          averageSentiment: normalizeScore(toneValue(entry, 'average')),
          averageShareOfVoice: normalizeScore(entry.averageShareOfVoice),
          averageRecommendation: normalizeScore(entry.averageRecommendation),
          terms: (entry.terms || [])
            .map((term) => ({
              term: resolveFacetTerm(term.term),
              count: term.count,
              visibility: normalizeScore(term.visibility),
              sentiment: normalizeScore(toneValue(term, 'own')),
              shareOfVoice: normalizeScore(term.shareOfVoice),
              recommendation: normalizeScore(term.recommendation),
              competitorVisibility: normalizeScore(term.competitorVisibility),
              competitorSentiment: normalizeScore(toneValue(term, 'competitor')),
              competitorShareOfVoice: normalizeScore(term.competitorShareOfVoice),
              competitorRecommendation: normalizeScore(term.competitorRecommendation),
              averageVisibility: normalizeScore(term.averageVisibility),
              averageSentiment: normalizeScore(toneValue(term, 'average')),
              averageShareOfVoice: normalizeScore(term.averageShareOfVoice),
              averageRecommendation: normalizeScore(term.averageRecommendation),
            }))
            .filter((term) => term.term !== undefined) as TrendRow['terms'],
        }));

        setTrendsRows(rows);
      } catch {
        if (active) {
          setTrendsRows(undefined);
          setTrendsFailed(true);
        }
      } finally {
        if (active) {
          setTrendsLoading(false);
        }
      }
    })();

    return () => {
      active = false;
      superseded.abort();
    };
  }, [api, displayMode, displayModeSettled, purposeResolved, statsFilters, kpisUnlocked, trendsAttempt]);

  // The Breakdown sits far down the screen: its request waits until the reader comes near it. The
  // band it watches mounts only once the skeleton is gone, which the callback ref picks up.
  const [breakdownNear, breakdownBandRef] = useNearViewport();
  // A purpose to measure is asked for as soon as the granularity settles, and a switched-on breakdown
  // once the reader also comes near it. Until a chart's first answer arrives it has nothing to draw
  // yet, which is not the same as an answer with nothing in it, so it shows the chart's loading shape,
  // not the empty chart. A failed request says so instead, and a later request keeps the last answer
  // on screen.
  const trendsAwaitingAnswer = kpisUnlocked && !trendsFailed && !trendsRows;
  const breakdownAwaitingAnswer = kpisUnlocked && breakdownFacets.length > 0 && !breakdownFailed && !breakdownHistory;

  React.useEffect(() => {
    // Also waits on `displayModeSettled` — see the Trends effect above.
    if (!api || !facetConfigs.length || !breakdownFacets.length || !kpisUnlocked || !displayModeSettled || !breakdownNear) {
      setBreakdownHistory(undefined);
      setBreakdownLoading(false);
      setBreakdownFailed(false);
      return;
    }

    let active = true;
    // See the headline figures above.
    const superseded = new AbortController();

    void (async () => {
      setBreakdownLoading(true);
      setBreakdownFailed(false);
      try {
        const selectedConfigs = facetConfigs.filter(
          (config): config is FacetConfig & { facet: BreakdownFacet } =>
            breakdownFacets.includes(config.facet as BreakdownFacet) && BREAKDOWN_FACETS.includes(config.facet)
        );

        if (!selectedConfigs.length) {
          setBreakdownHistory(undefined);
          return;
        }

        const filters = {
          ...statsFilters,
          facetTaxonomyKey: selectedConfigs.map((config) => config.taxonomyKey),
        };

        const history =
          displayMode === 'day'
            ? await scheduleStatsRequest({
                priority: StatsRequestPriority.Breakdown,
                signal: superseded.signal,
                ...promptMonitoringDailyHistoryRequest(api, { days: DAILY_HISTORY_BUCKETS, filters: filters }),
              })
            : displayMode === 'month'
              ? await scheduleStatsRequest({
                  priority: StatsRequestPriority.Breakdown,
                  signal: superseded.signal,
                  ...promptMonitoringMonthlyHistoryRequest(api, { months: HISTORY_BUCKETS, filters: filters }),
                })
              : await scheduleStatsRequest({
                  priority: StatsRequestPriority.Breakdown,
                  signal: superseded.signal,
                  ...promptMonitoringWeeklyHistoryRequest(api, { weeks: HISTORY_BUCKETS, filters: filters }),
                });

        if (active) {
          setBreakdownHistory({ labels: history.map(historyBucketLabel), buckets: history as StatsHistoryBucket[] });
        }
      } catch {
        if (active) {
          setBreakdownHistory(undefined);
          setBreakdownFailed(true);
        }
      } finally {
        if (active) {
          setBreakdownLoading(false);
        }
      }
    })();

    return () => {
      active = false;
      superseded.abort();
    };
    // Only what changes the request. The metric, the colours and the term names only change how
    // the answer is drawn, below, and switching them must not ask Sync Core again.
  }, [api, breakdownFacets, breakdownNear, displayMode, displayModeSettled, facetConfigs, statsFilters, kpisUnlocked, breakdownAttempt]);

  const breakdownSeries = useMemo((): BreakdownSeries[] => {
    if (!breakdownHistory) {
      return [];
    }
    const { buckets, labels: bucketLabels } = breakdownHistory;

    const termSeries = new Map<string, { id: string; label: string; valuesByBucket: Map<string, number> }>();

    buckets.forEach((bucket, index) => {
      const bucketLabel = bucketLabels[index];
      for (const term of bucket.terms || []) {
        const termKey = resolveFacetTerm(term.term);
        if (termKey === undefined || !isDisplayableStatsTerm(facetConfigs, termKey)) {
          continue;
        }

        const termId = termKey === null ? NULL_FACET_TERM_KEY : termKey;
        const termLabel = termKey === null ? NO_TERM_ASSIGNED_LABEL : resolveTermName(facetConfigs, termKey);
        // Through the shared binding, so all four metrics read their own fields under both
        // scopes and cannot drift apart. Undefined survives: a metric this row does not carry
        // is ABSENT, and the `|| 0` that used to sit here turned that absence into a flat zero
        // line — a claim ("never recommended") indistinguishable from "not returned".
        const value = breakdownMetricPercentOf(term, shownBreakdownKpi);

        const series = termSeries.get(termId) || {
          id: termId,
          label: termLabel,
          valuesByBucket: new Map<string, number>(),
        };
        if (value !== undefined) {
          series.valuesByBucket.set(bucketLabel, value);
        }
        termSeries.set(termId, series);
      }
    });

    const histories = Array.from(termSeries.values())
      .map((series) => ({
        id: series.id,
        label: series.label,
        // A bucket with no measurement stays undefined and the line breaks there, rather
        // than dropping to zero and asserting a reading nobody took.
        points: bucketLabels.map((label) => ({
          x: label,
          value: series.valuesByBucket.get(label),
        })),
      }))
      // Alphabetical within each ownership group; the grouping itself is applied below.
      .sort((left, right) => left.label.localeCompare(right.label));

    // Own entities lead and take the theme primary, through the same helpers the answers list
    // and answer detail use — so a brand is the same colour wherever the reader meets it.
    const pointsById = new Map(histories.map((entry) => [entry.id, entry.points] as const));
    const colored = orderAndColorBreakdownSeries(
      histories.map((entry) => ({
        id: entry.id,
        label: entry.label,
        facet: entry.id === NULL_FACET_TERM_KEY ? undefined : resolveTermFacet(facetConfigs, entry.id),
      })),
      primaryColor,
      GOVERNANCE_COMPARISON_PALETTE,
      storedTermColors
    );

    return colored.map((entry) => ({
      id: entry.id,
      label: entry.label,
      color: entry.color,
      points: pointsById.get(entry.id) ?? [],
    }));
  }, [breakdownHistory, facetConfigs, shownBreakdownKpi, primaryColor, storedTermColors]);

  React.useEffect(() => {
    if (!api || !gapsFacets.length || !facetConfigs.length || !kpisUnlocked) {
      setGapsLoading(false);
      setGapsError(false);
      return;
    }

    let active = true;
    // See the headline figures above.
    const superseded = new AbortController();

    void (async () => {
      setGapsLoading(true);
      setGapsError(false);
      try {
        const selectedConfigs = facetConfigs.filter((entry): entry is FacetConfig & { facet: BreakdownFacet } =>
          gapsFacets.includes(entry.facet as BreakdownFacet)
        );
        if (!selectedConfigs.length) {
          return;
        }

        const facetTaxonomyKey = selectedConfigs.map((entry) => entry.taxonomyKey);
        const gapsFilters = { ...statsFilters, facetTaxonomyKey };

        const [result, contentHealth] = await Promise.all([
          scheduleStatsRequest({
            ...promptMonitoringTotalRequest(api, gapsFilters),
            priority: StatsRequestPriority.Landscape,
            signal: superseded.signal,
          }),
          // The content figures are an aggregation too, so they wait for a place like the others.
          scheduleStatsRequest({
            key: statsRequestKey({ method: 'contentStats.total', parameters: [ContentRepresentation.Page, gapsFilters] }),
            priority: StatsRequestPriority.Landscape,
            signal: superseded.signal,
            run: () => api.governance.stats.total(ContentRepresentation.Page, gapsFilters),
          }),
        ]);

        if (!active) {
          return;
        }

        const aiRows = (result.terms || [])
          .map((entry) => ({
            term: resolveFacetTerm(entry.term),
            count: entry.count,
            answerCount: normalizeCount(entry.answerCount),
            visibility: normalizeScore(entry.visibility ?? entry.competitorVisibility),
            sentiment: normalizeScore(toneValue(entry, 'own') ?? toneValue(entry, 'competitor')),
            shareOfVoice: normalizeScore(entry.shareOfVoice ?? entry.competitorShareOfVoice),
            recommendation: normalizeScore(entry.recommendation ?? entry.competitorRecommendation),
            ...subjectPositionOf(entry),
          }))
          .filter((entry) => entry.term !== undefined) as GapsRow[];

        const healthRows = (contentHealth.terms || [])
          .map((entry) => ({
            term: resolveFacetTerm(entry.term),
            averageScore: normalizeScore(entry.averageScore),
            count: entry.count,
          }))
          .filter((entry) => entry.term !== undefined) as Array<{ term: string | null; averageScore?: number; count: number }>;

        const labelFromTerm = (term: string | null): string =>
          term === null ? NO_TERM_ASSIGNED_LABEL : resolveTermName(facetConfigs, term);

        const healthByLabel = new Map<string, { averageScore?: number; term: string | null; count: number }>();
        for (const row of healthRows) {
          healthByLabel.set(labelFromTerm(row.term), { averageScore: row.averageScore, term: row.term, count: row.count });
        }

        const nextRows = aiRows
          .filter((row) => isDisplayableStatsTerm(facetConfigs, row.term))
          .map((row) => {
            const label = labelFromTerm(row.term);
            const health = healthByLabel.get(label);
            return {
              ...row,
              contentHealth: health?.averageScore,
            };
          });

        const existingLabels = new Set(nextRows.map((row) => labelFromTerm(row.term)));
        // Terms with content but no AI answers are REAL gaps — the point of this table — so they are
        // kept rather than dropped. Their AI metrics are genuinely unmeasured, and the renderer shows
        // that rather than coercing it to a zero bar. They go through the same displayability filter
        // as every other row, which they previously bypassed.
        const missingHealthRows = healthRows
          .filter((row) => !existingLabels.has(labelFromTerm(row.term)) && isDisplayableStatsTerm(facetConfigs, row.term))
          .map((row) => ({
            term: row.term,
            count: 0,
            visibility: undefined,
            sentiment: undefined,
            shareOfVoice: undefined,
            contentHealth: row.averageScore,
          }));

        nextRows.push(...missingHealthRows);

        setGapsRows(nextRows);
      } catch {
        // The section shows its failure, so the other read, if still waiting for its turn — as it
        // does while a failure holds the queue back — is dropped rather than sent for nothing.
        superseded.abort();
        // The rows on screen belong to the selection that last succeeded, not to this one, so they
        // are cleared rather than left to read as current under a tab they were never fetched for.
        if (active) {
          setGapsRows([]);
          setGapsError(true);
        }
      } finally {
        if (active) {
          setGapsLoading(false);
        }
      }
    })();

    return () => {
      active = false;
      superseded.abort();
    };
  }, [api, facetConfigs, gapsFacets, statsFilters, kpisUnlocked, gapsAttempt]);

  // Once the first stage is done the prompt list is due for good: a purpose change that asks for
  // headline figures again never takes it back, nor abandons a list already on its way.
  React.useEffect(() => {
    if (headlineSettled) {
      setPromptListDue(true);
    }
  }, [headlineSettled]);

  React.useEffect(() => {
    // The prompts only fill the Prompt filter, and nothing else on the screen waits for them. It
    // is asked for once the screen stands and the headline figures are in, and it is sent only
    // when no section waits for its figures, never in one of their places. A selection the address
    // carries filters the figures from the start; the filter shows it once the list arrives.
    if (!api || loading || loadFailed || !promptListDue) {
      return;
    }

    let active = true;
    const superseded = new AbortController();
    const promptFilters: MonitoringPromptSummaryListFilters = { status: NON_PREVIOUS_PROMPT_STATUSES };
    setPromptOptionsFailed(false);
    void (async () => {
      try {
        // The lean list: each prompt's key, name and purposes, every page of it, so no prompt is left
        // out of the filter. Each page waits for its turn in the statistics queue like any request.
        const summaries: MonitoringPromptSummaryDto[] = [];
        let numberOfPages = 1;
        for (let page = 0; page < numberOfPages; page += 1) {
          const pagination = { page, itemsPerPage: PROMPT_SUMMARY_PAGE_SIZE };
          const response = await scheduleStatsRequest({
            key: statsRequestKey({ method: 'monitoringPrompts.listSummaries', parameters: [pagination, promptFilters] }),
            priority: StatsRequestPriority.PromptFilter,
            background: true,
            signal: superseded.signal,
            run: () => api.governance.monitoringPrompts.listSummaries(pagination, promptFilters),
          });
          const items = response.items || [];
          summaries.push(...items);
          // Read from each answer, which counts the pages at the size the list actually served.
          numberOfPages = response.numberOfPages ?? 1;
          if (!items.length) {
            break;
          }
        }
        if (active) {
          setPromptOptions(scopablePromptsOf(summaries));
        }
      } catch {
        if (active) {
          setPromptOptionsFailed(true);
        }
      }
    })();

    return () => {
      active = false;
      superseded.abort();
    };
  }, [api, loading, loadFailed, promptListDue, promptOptionsAttempt]);

  // The Gaps buttons' own labels, which is what the Brand landscape header points the reader at.
  const gapsFacetNames = useMemo(
    () => facetConfigs.filter((config) => gapsFacets.includes(config.facet as BreakdownFacet)).map((config) => config.taxonomyName),
    [facetConfigs, gapsFacets]
  );

  // Ranked over the rows in front of the reader, so "#1" means first within the current filter —
  // the same population the rest of the row's numbers are drawn from.
  const gapsPositionRanks = useMemo(() => rankSubjectsByPosition(gapsRows), [gapsRows]);

  const sortedGapsRows = useMemo(() => {
    const rows = [...gapsRows];
    rows.sort((left, right) => {
      if (shownGapsSortBy === 'term') {
        const leftLabel = left.term || '';
        const rightLabel = right.term || '';
        const result = leftLabel.localeCompare(rightLabel);
        return gapsSortDirection === 'asc' ? result : -result;
      }

      const leftValue =
        shownGapsSortBy === 'count'
          ? left.count
          : shownGapsSortBy === 'visibility'
            ? (left.visibility ?? 0)
            : isToneKpi(shownGapsSortBy)
              ? (left.sentiment ?? 0)
              : shownGapsSortBy === 'shareOfVoice'
                ? (left.shareOfVoice ?? 0)
                : shownGapsSortBy === 'recommendation'
                  ? (left.recommendation ?? 0)
                  : (left.contentHealth ?? 0);
      const rightValue =
        shownGapsSortBy === 'count'
          ? right.count
          : shownGapsSortBy === 'visibility'
            ? (right.visibility ?? 0)
            : isToneKpi(shownGapsSortBy)
              ? (right.sentiment ?? 0)
              : shownGapsSortBy === 'shareOfVoice'
                ? (right.shareOfVoice ?? 0)
                : shownGapsSortBy === 'recommendation'
                  ? (right.recommendation ?? 0)
                  : (right.contentHealth ?? 0);

      const result = leftValue - rightValue;
      return gapsSortDirection === 'asc' ? result : -result;
    });
    return rows;
  }, [gapsRows, shownGapsSortBy, gapsSortDirection]);

  // The reference marker on each bar is the mean of the ROWS ON SCREEN, not the KPI-card totals:
  // the gaps fetch adds a facet filter the summary fetch does not, so a marker drawn from the
  // KPI-card population would compare each bar against a population that is not the table in
  // front of them.
  /**
   * The largest measured value per metric among the rows on screen.
   *
   * Only the relative-axis columns use it, and it must come from the same population as the
   * averages: a bar scaled to a maximum drawn from rows the reader cannot see would make two rows
   * of equal value look different.
   */
  const gapsRowMaxima = useMemo(
    () => ({
      recommendation: maximumOfMeasured(sortedGapsRows, (row) => row.recommendation),
      // Share of voice divides across every brand in an answer, so on a project tracking a dozen
      // it never approaches 100% and a 0-100 bar leaves every row looking equally empty. Scaled to
      // the highest on screen, the column compares rows to each other, which is the comparison the
      // number actually supports.
      shareOfVoice: maximumOfMeasured(sortedGapsRows, (row) => row.shareOfVoice),
    }),
    [sortedGapsRows]
  );

  const gapsRowAverages = useMemo(
    () => ({
      visibility: averageOfMeasured(sortedGapsRows, (row) => row.visibility),
      sentiment: averageOfMeasured(sortedGapsRows, (row) => row.sentiment),
      shareOfVoice: averageOfMeasured(sortedGapsRows, (row) => row.shareOfVoice),
      recommendation: averageOfMeasured(sortedGapsRows, (row) => row.recommendation),
    }),
    [sortedGapsRows]
  );

  const applyGapsSort = (
    column: 'term' | 'count' | 'visibility' | ToneMetricName | 'shareOfVoice' | 'recommendation' | 'contentHealth'
  ) => {
    if (shownGapsSortBy === column) {
      setGapsSortDirection((value) => (value === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setGapsSortBy(column);
    setGapsSortDirection(column === 'term' ? 'asc' : 'desc');
  };

  if (loading) {
    return <AnalyticsScreenSkeleton label="Loading AI Visibility" kpiCount={5} />;
  }

  const pageHeader = (
    <BrandPresencePageHeader
      eyebrow={
        <>
          Brand Presence <span className="text-slate-300">/</span> Analytics
        </>
      }
      title="AI Visibility"
      description="How AI assistants answer your prompts: whether they mention your brand, how they speak about it, and whom they recommend."
    />
  );

  if (loadFailed) {
    return (
      <div className="relative w-full h-full bg-slate-50 text-slate-900">
        <main className="relative max-w-[1800px] mx-auto px-6 py-8">
          {pageHeader}
          <SectionLoadFailed message={SCREEN_LOAD_FAILED_MESSAGE} onRetry={retryLoad} />
        </main>
      </div>
    );
  }

  // What this tab's own favicons (the Sources card) wait for: not a timer, but every request this
  // screen starts on its own having landed — the bootstrap term lists and the KPI, Trends, Breakdown
  // and Gaps stats fetches. Before this landed, an uncached favicon competed with exactly those
  // requests for the pod and the browser's per-origin connection budget (46.5 s on one miss,
  // measured). `loading` is always false past the guard above; it is named anyway so this
  // reads as the complete condition rather than one that happens to rely on an earlier return.
  const faviconLoadReady = !loading && !summaryLoading && !trendsLoading && !breakdownLoading && !gapsLoading;

  return (
    <FaviconLoadGate ready={faviconLoadReady}>
      <div className="relative w-full h-full bg-slate-50 text-slate-900 bg-brand-page">
        <main className="relative max-w-[1800px] min-h-[800px] mx-auto px-6 py-8">
          {pageHeader}
          {purposeTabs.length ? (
            <div className="mb-5 flex flex-wrap gap-1 border-b border-slate-200/80" role="tablist" aria-label="Prompt purpose">
              {purposeTabs.map((tab) => {
                const active = tab.key === selectedPurposeTermKey;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    title={tab.presentation.title}
                    className={getBrandPresenceTabClassName(active, ['px-3 py-2.5', tab.presentation.className].filter(Boolean).join(' '))}
                    onClick={() => setSelectedPurposeTermKey(tab.key)}
                  >
                    {tab.name}
                    {tab.presentation.showSuggestionIcon ? <SuggestedTermIcon /> : null}
                  </button>
                );
              })}
            </div>
          ) : null}
          <div className={`${BP_CARD_CLASSES} mb-6 px-4 py-3 motion-safe:animate-bp-fade-up`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <span className={FILTER_LABEL_CLASSES}>Filters</span>
                <div className="d-flex align-items-center gap-2">
                  <span className="text-sm font-semibold text-slate-600">Engine</span>
                  <Form.Select
                    size="sm"
                    value={selectedEngineKey}
                    style={{ minWidth: '180px' }}
                    onChange={(event) => setSelectedEngineKey(event.target.value)}
                  >
                    <option value="">All engines</option>
                    {engineOptions.map((option) => (
                      <option key={option.key} value={option.key} className={option.presentation.className}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span className="text-sm font-semibold text-slate-600">Locale</span>
                  <Form.Select
                    size="sm"
                    value={selectedLocaleKey}
                    style={{ minWidth: '180px' }}
                    onChange={(event) => setSelectedLocaleKey(event.target.value)}
                  >
                    <option value="">All locales</option>
                    {localeOptions.map((option) => (
                      <option key={option.key} value={option.key} className={option.presentation.className}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span className="text-sm font-semibold text-slate-600">Prompt</span>
                  <Form.Select
                    size="sm"
                    value={selectedPromptKey}
                    style={{ minWidth: '220px' }}
                    onChange={(event) => setSelectedPromptKey(event.target.value)}
                  >
                    <option value="">All prompts</option>
                    {scopedPromptOptions.map((option) => (
                      <option key={option.key} value={option.key}>
                        {option.name}
                      </option>
                    ))}
                  </Form.Select>
                  {/* Without the list the filter can only offer "All prompts"; it says why. */}
                  {promptOptionsFailed ? <SectionLoadFailed message={PROMPTS_LOAD_FAILED_MESSAGE} onRetry={retryPromptOptions} /> : null}
                </div>
                <AddFilterDropdown
                  api={api}
                  selectedFilters={selectedFilters}
                  onChange={setSelectedFilters}
                  allowedTargetTypes={['taxonomy']}
                />
              </div>
              <button type="button" className={BP_TOOLBAR_BUTTON_CLASSES} onClick={() => setSelectedFilters([])}>
                Reset filters
              </button>
            </div>
          </div>

          {!hasSelectedPurpose ? (
            <div className={`${BP_CARD_CLASSES} px-6 py-14 text-center motion-safe:animate-bp-fade-up`}>
              <div className="mb-1 text-base font-semibold text-slate-900">Choose a prompt purpose to view metrics</div>
              <div className="mx-auto max-w-md text-sm leading-relaxed text-slate-500">
                {/* The page selects the highest-priority purpose on load, so reaching this state means
                  there are no purposes to select — not that the operator has yet to pick one. */}
                {purposeTabs.length
                  ? 'AI Visibility metrics are only meaningful within a single prompt purpose. Pick one above to continue.'
                  : 'No prompt purposes are available yet. Create a “Prompt purpose” taxonomy and assign purposes to your monitoring prompts first.'}
              </div>
            </div>
          ) : selectedPurposeUntyped ? (
            <UntypedPurposeNotice
              purposeName={blockedPurposeTab?.name ?? ''}
              onSetType={blockedPurposePath ? () => navigate(blockedPurposePath) : undefined}
            />
          ) : (
            <>
              {/* Says what this purpose measures, so a screen that has lost a card does not read as a
                fault. Renders nothing when the purpose measures everything. */}
              <PurposeKpiScopeNote term={selectedPurpose} className="mb-4" />
              {summaryFailed ? (
                <div className="mb-4">
                  <SectionLoadFailed message={KPIS_LOAD_FAILED_MESSAGE} onRetry={retrySummary} />
                </div>
              ) : null}
              <GovernanceAnalyticsKpiCardRow>
                {/* A KPI the purpose fixes by construction gets no card at all: an empty card, or one
                  reading 100%, presents the question back to the reader as a result. */}
                {showsVisibility ? (
                  <GovernanceAnalyticsKpiCard
                    title="Avg Visibility"
                    value={formatPercentage(avgVisibility)}
                    loading={summaryLoading}
                    info="Share of AI answers in which this brand appears at all."
                  />
                ) : null}
                {showsShareOfVoice ? (
                  <GovernanceAnalyticsKpiCard
                    title="Avg Share of Voice"
                    value={formatPercentage(avgShareOfVoice)}
                    loading={summaryLoading}
                    info="How much of each answer is about this brand, averaged across answers."
                  />
                ) : null}
                {showsSentiment ? (
                  <GovernanceAnalyticsKpiCard
                    title={`Avg ${TONE_METRIC_LABEL}`}
                    value={formatPercentage(avgSentiment)}
                    // The one KPI here that is read against bands. Visibility, share of voice and
                    // recommendation are shares of a population, not favourability, so they get no icon.
                    bandMetric={TONE_BAND_METRIC}
                    bandValue={avgSentiment}
                    loading={summaryLoading}
                    info="How positive the AI's tone is toward this brand, on average."
                  />
                ) : null}
                {showsRecommendation ? (
                  <GovernanceAnalyticsKpiCard
                    title="Recommendation rate"
                    value={formatPercentage(avgRecommendation)}
                    loading={summaryLoading}
                    info="Share of answers in which AI explicitly recommends this brand as the winner."
                    onClick={() =>
                      navigate(
                        `/brand-presence/analytics/ai-answers?${new URLSearchParams({
                          ...(selectedPurposeTermKey ? { purpose: selectedPurposeTermKey } : {}),
                          recommendation: 'us',
                        }).toString()}`
                      )
                    }
                  />
                ) : null}
                {/* Never masked: it counts what was measured rather than measuring anything, so every
                  purpose has it. */}
                <GovernanceAnalyticsKpiCard
                  title="AI answers analyzed"
                  // A dash rather than a zero when the deployment reports no count: "0 answers
                  // analyzed" is a measurement, and no measurement was returned.
                  value={analyzedPromptRunCount === undefined ? '-' : String(analyzedPromptRunCount)}
                  subtitle={`${displayMode === 'day' ? DAILY_HISTORY_BUCKETS : HISTORY_BUCKETS} ${displayMode === 'day' ? 'days' : displayMode === 'month' ? 'months' : 'weeks'} window`}
                  loading={summaryLoading}
                  info="How many monitored-prompt answers this window analyzed. One answer is one run of one prompt against one engine; answers still queued, or that failed before analysis, are not counted."
                  onClick={() =>
                    navigate(
                      `/brand-presence/analytics/ai-answers${
                        selectedPurposeTermKey ? `?${new URLSearchParams({ purpose: selectedPurposeTermKey }).toString()}` : ''
                      }`
                    )
                  }
                />
              </GovernanceAnalyticsKpiCardRow>

              <div className="space-y-6">
                {/* Only for a purpose that measures BOTH figures: a landscape missing an axis is
                  not a smaller landscape, and the domain lookup behind the tiles never fires for a
                  purpose whose tiles are not drawn. */}
                {showsVisibility && showsSentiment ? (
                  <BrandLandscapeCard
                    api={api}
                    rows={gapsRows}
                    facetConfigs={facetConfigs}
                    facetNames={gapsFacetNames}
                    loading={gapsLoading}
                    error={gapsError}
                    onRetry={retryGaps}
                    purposeTermKey={selectedPurposeTermKey}
                  />
                ) : null}

                {/* No card at all when the purpose measures none of the four metrics a line chart
                  plots — an axis and a legend with no series is not a smaller chart. */}
                {chartMetrics.length ? (
                  <GovernanceAnalyticsSectionCard
                    title="Trends"
                    loading={trendsLoading || trendsAwaitingAnswer}
                    actions={
                      <>
                        <GovernanceSegmentedSwitch
                          value={trendsComparisonMode}
                          onChange={setTrendsComparisonMode}
                          options={[
                            { value: 'competitor', label: 'Competitor' },
                            { value: 'average', label: 'Average' },
                          ]}
                        />
                        <GovernanceSegmentedSwitch
                          value={displayMode}
                          onChange={chooseDisplayMode}
                          options={[
                            { value: 'day', label: 'Day' },
                            { value: 'month', label: 'Month' },
                            { value: 'week', label: 'Week' },
                          ]}
                        />
                      </>
                    }
                  >
                    {trendsFailed ? (
                      <SectionLoadFailed message={TRENDS_LOAD_FAILED_MESSAGE} onRetry={retryTrends} />
                    ) : trendsAwaitingAnswer ? (
                      <TrendChartSkeleton />
                    ) : (
                      <TrendsVisualization
                        rows={trendsRows ?? []}
                        selectedFacet="none"
                        selectedKpi={shownTrendsKpi}
                        comparisonMode={trendsComparisonMode}
                        facetConfigs={facetConfigs}
                        metrics={chartMetrics}
                      />
                    )}
                  </GovernanceAnalyticsSectionCard>
                ) : null}

                {/* The sections below load as the reader scrolls towards them, not with the screen.
                  One the screen already knows to be empty holds no room: a purpose that does not
                  measure attributes, or a window without a single analyzed answer for Sources. */}
                {showsAttributes ? (
                  <LoadWhenNearViewport placeholderClassName={SECTION_PLACEHOLDER_CLASSES}>
                    <GovernanceAnalyticsAttributes
                      api={api}
                      statsFilters={statsFilters}
                      hasSelectedPurpose={hasSelectedPurpose}
                      attributesAllowed={showsAttributes}
                      hideSuggestions={hideSuggestions}
                      onHideSuggestionsChange={setHideSuggestions}
                      view={attributeView}
                      onViewChange={setAttributeView}
                      facetConfigs={facetConfigs}
                    />
                  </LoadWhenNearViewport>
                ) : null}

                {/* Sources — after Attributes, before Gaps. */}
                {summaryLoading || analyzedPromptRunCount !== 0 ? (
                  <LoadWhenNearViewport placeholderClassName={SECTION_PLACEHOLDER_CLASSES}>
                    <GovernanceAnalyticsSources
                      api={api}
                      statsFilters={statsFilters}
                      hasSelectedPurpose={hasSelectedPurpose}
                      analyzedPromptRunCount={analyzedPromptRunCount}
                      analyzedPromptRunsLoading={summaryLoading}
                      granularity={displayMode}
                      grouping={sourceGrouping}
                      onGroupingChange={setSourceGrouping}
                    />
                  </LoadWhenNearViewport>
                ) : null}

                {chartMetrics.length ? (
                  <div className="relative">
                    <NearViewportBand bandRef={breakdownBandRef} />
                    <GovernanceAnalyticsSectionCard
                      title="Breakdown"
                      // Until the reader comes near, the card waits for its figures like a loading one.
                      loading={breakdownLoading || !breakdownNear || breakdownAwaitingAnswer}
                      actions={
                        <>
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            {facetConfigs
                              .filter((config) => BREAKDOWN_FACETS.includes(config.facet))
                              .map((config) => {
                                const isActive = breakdownFacets.includes(config.facet);
                                return (
                                  <button
                                    key={`breakdown-facet:${config.facet}`}
                                    type="button"
                                    className={`px-3 py-1.5 text-xs rounded border ${
                                      isActive ? '' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                                    }`}
                                    style={isActive ? activeFacetChipStyle : undefined}
                                    onClick={() =>
                                      setBreakdownFacets((current) =>
                                        current.includes(config.facet)
                                          ? current.filter((entry) => entry !== config.facet)
                                          : [...current, config.facet]
                                      )
                                    }
                                  >
                                    {config.taxonomyName}
                                  </button>
                                );
                              })}
                          </div>
                          {/* Only the metrics this purpose measures are offered — a suppressed one is
                          not an option the reader can pick, so it can never become the selection. */}
                          <GovernanceSegmentedSwitch
                            value={shownBreakdownKpi}
                            onChange={setBreakdownKpi}
                            options={breakdownMetricOptions}
                          />
                          <GovernanceSegmentedSwitch
                            value={displayMode}
                            onChange={chooseDisplayMode}
                            options={[
                              { value: 'day', label: 'Day' },
                              { value: 'month', label: 'Month' },
                              { value: 'week', label: 'Week' },
                            ]}
                          />
                        </>
                      }
                    >
                      {breakdownFailed ? (
                        <SectionLoadFailed message={BREAKDOWN_LOAD_FAILED_MESSAGE} onRetry={retryBreakdown} />
                      ) : breakdownAwaitingAnswer ? (
                        <TrendChartSkeleton />
                      ) : (
                        <BreakdownVisualization
                          series={breakdownSeries}
                          kpi={shownBreakdownKpi}
                          breakdownEnabled={breakdownFacets.length > 0}
                        />
                      )}
                    </GovernanceAnalyticsSectionCard>
                  </div>
                ) : null}

                <GovernanceAnalyticsSectionCard
                  title="Gaps"
                  loading={gapsLoading}
                  actions={
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      {facetConfigs
                        .filter((config) => BREAKDOWN_FACETS.includes(config.facet))
                        .map((config) => {
                          const isActive = gapsFacets.includes(config.facet as BreakdownFacet);
                          return (
                            <button
                              key={`gaps-facet:${config.facet}`}
                              type="button"
                              className={`px-3 py-1.5 text-xs rounded border ${
                                isActive ? '' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                              }`}
                              style={isActive ? activeFacetChipStyle : undefined}
                              onClick={() =>
                                setGapsFacets((current) => {
                                  const facet = config.facet as BreakdownFacet;
                                  return current.includes(facet) ? current.filter((entry) => entry !== facet) : [...current, facet];
                                })
                              }
                            >
                              {config.taxonomyName}
                            </button>
                          );
                        })}
                    </div>
                  }
                >
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-slate-50/70 border-b border-slate-200/70">
                          <th className="p-3 text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-slate-500 whitespace-nowrap min-w-[220px]">
                            <GovernanceSortableHeader
                              label="Tag"
                              isActive={shownGapsSortBy === 'term'}
                              direction={gapsSortDirection}
                              onClick={() => applyGapsSort('term')}
                            />
                          </th>
                          <th className="p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider w-24">
                            <GovernanceSortableHeader
                              label="# Prompts"
                              isActive={shownGapsSortBy === 'count'}
                              direction={gapsSortDirection}
                              onClick={() => applyGapsSort('count')}
                            />
                          </th>
                          {/* A metric this purpose does not measure loses its whole column, header and
                            cells together. A column of dashes reads as "measured, found nothing". */}
                          {showsVisibility ? (
                            <th className="p-3 text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-slate-500 whitespace-nowrap min-w-[220px]">
                              <GovernanceSortableHeader
                                label="Visibility"
                                isActive={shownGapsSortBy === 'visibility'}
                                direction={gapsSortDirection}
                                onClick={() => applyGapsSort('visibility')}
                              />
                            </th>
                          ) : null}
                          {showsSentiment ? (
                            <th className="p-3 text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-slate-500 whitespace-nowrap min-w-[220px]">
                              <GovernanceSortableHeader
                                label={TONE_METRIC_LABEL}
                                isActive={isToneKpi(shownGapsSortBy)}
                                direction={gapsSortDirection}
                                onClick={() => applyGapsSort(TONE_METRIC)}
                              />
                            </th>
                          ) : null}
                          {showsShareOfVoice ? (
                            <th className="p-3 text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-slate-500 whitespace-nowrap min-w-[220px]">
                              <GovernanceSortableHeader
                                label="Share of Voice"
                                isActive={shownGapsSortBy === 'shareOfVoice'}
                                direction={gapsSortDirection}
                                onClick={() => applyGapsSort('shareOfVoice')}
                              />
                            </th>
                          ) : null}
                          {showsRecommendation ? (
                            <th className="p-3 text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-slate-500 whitespace-nowrap min-w-[220px]">
                              <GovernanceSortableHeader
                                label="Recommendation"
                                isActive={shownGapsSortBy === 'recommendation'}
                                direction={gapsSortDirection}
                                onClick={() => applyGapsSort('recommendation')}
                              />
                            </th>
                          ) : null}
                          {/* Not sortable: the column already presents its own ordering, and a
                            second one over the same numbers would only disagree with it. */}
                          {showsPosition ? (
                            <th className="p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider w-32">Position</th>
                          ) : null}
                          <th className="p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider min-w-[160px]">
                            <GovernanceSortableHeader
                              label="Content Health"
                              isActive={shownGapsSortBy === 'contentHealth'}
                              direction={gapsSortDirection}
                              onClick={() => applyGapsSort('contentHealth')}
                            />
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 bg-white">
                        {sortedGapsRows.map((row) => {
                          // Resolved once per row so every cell's tooltip names the same subject the
                          // row label shows, rather than saying "this term".
                          const gapsRowSubject = row.term === null ? NO_TERM_ASSIGNED_LABEL : resolveTermName(facetConfigs, row.term);
                          return (
                            <tr key={`gaps:${row.term || NULL_FACET_TERM_KEY}`}>
                              <td className="p-3 text-sm font-semibold">
                                {row.term ? (
                                  <button
                                    type="button"
                                    className="text-left hover:underline"
                                    title="View the AI answers behind this row"
                                    onClick={() =>
                                      navigate(
                                        `/brand-presence/analytics/ai-answers?${new URLSearchParams({
                                          ...(selectedPurposeTermKey ? { purpose: selectedPurposeTermKey } : {}),
                                          recommendedTerm: row.term as string,
                                        }).toString()}`
                                      )
                                    }
                                  >
                                    <FacetTermName facetConfigs={facetConfigs} term={row.term} />
                                  </button>
                                ) : (
                                  <FacetTermName facetConfigs={facetConfigs} term={row.term} />
                                )}
                              </td>
                              <td className="p-3 text-sm text-slate-700">{row.count}</td>
                              {/* Named once, used by every metric cell in the row. Each cell is present
                                exactly when its header is, so no row can outrun the head. */}
                              {showsVisibility ? (
                                <GapsMetricCell
                                  value={row.visibility}
                                  average={gapsRowAverages.visibility}
                                  color={VISIBILITY_SERIES_COLOR}
                                  opacity={0.55}
                                  label="visibility"
                                  subject={gapsRowSubject}
                                />
                              ) : null}
                              {showsSentiment ? (
                                <GapsMetricCell
                                  value={row.sentiment}
                                  average={gapsRowAverages.sentiment}
                                  color={SENTIMENT_SERIES_COLOR}
                                  opacity={0.48}
                                  label={TONE_METRIC_LABEL_LOWER}
                                  subject={gapsRowSubject}
                                  bandMetric={TONE_BAND_METRIC}
                                  variant="band"
                                />
                              ) : null}
                              {showsShareOfVoice ? (
                                <GapsMetricCell
                                  value={row.shareOfVoice}
                                  average={gapsRowAverages.shareOfVoice}
                                  color={primaryColor}
                                  opacity={0.52}
                                  label="share of voice"
                                  subject={gapsRowSubject}
                                  variant="relative"
                                  displayedMaximum={gapsRowMaxima.shareOfVoice}
                                />
                              ) : null}
                              {showsRecommendation ? (
                                <GapsMetricCell
                                  value={row.recommendation}
                                  average={gapsRowAverages.recommendation}
                                  color="#EE7402"
                                  opacity={0.5}
                                  label="recommendation"
                                  subject={gapsRowSubject}
                                  variant="relative"
                                  displayedMaximum={gapsRowMaxima.recommendation}
                                />
                              ) : null}
                              {showsPosition ? (
                                <td className="p-3 text-sm text-slate-700">
                                  <PositionRankBadge
                                    untracked={row.term === null}
                                    rank={row.term === null ? undefined : gapsPositionRanks.get(row.term)}
                                  />
                                </td>
                              ) : null}
                              <td className="p-3 text-sm text-slate-700">
                                {row.contentHealth === undefined ? '-' : formatPercentage(row.contentHealth)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {!sortedGapsRows.length ? (
                      gapsError ? (
                        <div className="mt-3">
                          <SectionLoadFailed message={GAPS_REFRESH_FAILED_MESSAGE} onRetry={retryGaps} />
                        </div>
                      ) : gapsLoading ? (
                        // Rows that have not arrived are not rows that are not there.
                        <div className="mt-3">
                          <TableRowsSkeleton />
                        </div>
                      ) : (
                        <div className="text-sm text-slate-500 mt-3">No data available.</div>
                      )
                    ) : null}
                  </div>
                </GovernanceAnalyticsSectionCard>
              </div>
            </>
          )}
        </main>
      </div>
    </FaviconLoadGate>
  );
}
